import { create } from 'zustand';

export type DoDThemeMode = 'DARK_INTELLIGENCE' | 'LIGHT_EXECUTIVE';

export interface DoDThemeTokens {
  bgOverlay: string;
  bgHeader: string;
  bgCard: string;
  bgCardHover: string;
  textPrimary: string;
  textSecondary: string;
  borderSubtle: string;
  borderHighlight: string;
  accentCyan: string;
  accentOrange: string; // Used for procurement data
}

export const DARK_THEME: DoDThemeTokens = {
  bgOverlay: 'bg-[#090d16]',
  bgHeader: 'bg-[#0b101c]',
  bgCard: 'bg-slate-900/80',
  bgCardHover: 'hover:bg-slate-900/90',
  textPrimary: 'text-slate-100',
  textSecondary: 'text-slate-400',
  borderSubtle: 'border-slate-800/80',
  borderHighlight: 'border-cyan-500/50',
  accentCyan: 'text-cyan-400',
  accentOrange: 'text-orange-400',
};

export const LIGHT_THEME: DoDThemeTokens = {
  bgOverlay: 'bg-slate-100',
  bgHeader: 'bg-white',
  bgCard: 'bg-white/90',
  bgCardHover: 'hover:bg-white',
  textPrimary: 'text-slate-900',
  textSecondary: 'text-slate-600',
  borderSubtle: 'border-slate-200',
  borderHighlight: 'border-cyan-600/60',
  accentCyan: 'text-cyan-700',
  accentOrange: 'text-orange-600',
};

export interface DoDThemeStoreState {
  themeMode: DoDThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: DoDThemeMode) => void;
  tokens: DoDThemeTokens;
}

export const THEME_STORAGE_KEY = 'wingman-theme-mode';

export function getInitialTheme(): DoDThemeMode {
  if (typeof window === 'undefined') return 'DARK_INTELLIGENCE';
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'LIGHT_EXECUTIVE' || stored === 'DARK_INTELLIGENCE') {
      return stored;
    }
  } catch {
    // Storage access unavailable or denied
  }
  return 'DARK_INTELLIGENCE';
}

export function syncDomTheme(mode: DoDThemeMode): void {
  if (typeof document === 'undefined') return;
  const isLight = mode === 'LIGHT_EXECUTIVE';
  const targetTheme = isLight ? 'light' : 'dark';
  document.documentElement.dataset.theme = targetTheme;
  document.documentElement.classList.remove('light', 'dark');
  document.documentElement.classList.add(targetTheme);
}

export function persistTheme(mode: DoDThemeMode): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Storage access unavailable or denied
  }
}

const initialTheme = getInitialTheme();
syncDomTheme(initialTheme);

export const useDoDThemeStore = create<DoDThemeStoreState>((set, get) => ({
  themeMode: initialTheme,
  tokens: initialTheme === 'LIGHT_EXECUTIVE' ? LIGHT_THEME : DARK_THEME,
  toggleTheme: () => {
    const next = get().themeMode === 'DARK_INTELLIGENCE' ? 'LIGHT_EXECUTIVE' : 'DARK_INTELLIGENCE';
    syncDomTheme(next);
    persistTheme(next);
    set({ themeMode: next, tokens: next === 'DARK_INTELLIGENCE' ? DARK_THEME : LIGHT_THEME });
  },
  setTheme: (mode: DoDThemeMode) => {
    const safeMode = mode === 'LIGHT_EXECUTIVE' ? 'LIGHT_EXECUTIVE' : 'DARK_INTELLIGENCE';
    syncDomTheme(safeMode);
    persistTheme(safeMode);
    set({ themeMode: safeMode, tokens: safeMode === 'DARK_INTELLIGENCE' ? DARK_THEME : LIGHT_THEME });
  },
}));

if (typeof window !== 'undefined') {
  (window as any).useThemeStore = useDoDThemeStore;
}
